// pages/Removals/Removals.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import {
  fetchRemovals,
  fetchDealerRemovals,
  restoreClient,
  getUninstallationForm,
  getCSVLogs,
  getTextLogs,
  selectRemovals,
  selectRemovalsLoading,
  selectRemovalsError,
  selectTotalRemovals,
  selectCSVLogs,
  selectTextLogs,
  selectLogsLoading,
  selectPDFUrl,
  selectPDFLoading,
  clearLogs,
  clearPDF
} from '../../features/removals/removalsSlice';
import './removals.css';

const Removals = () => {
  const dispatch = useDispatch();
  const removals = useSelector(selectRemovals);
  const loading = useSelector(selectRemovalsLoading);
  const error = useSelector(selectRemovalsError);
  const totalRecords = useSelector(selectTotalRemovals);
  const csvLogs = useSelector(selectCSVLogs);
  const textLogs = useSelector(selectTextLogs);
  const logsLoading = useSelector(selectLogsLoading);
  const pdfUrl = useSelector(selectPDFUrl);
  const pdfLoading = useSelector(selectPDFLoading);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPage, setGoToPage] = useState('');
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [logType, setLogType] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');

  useEffect(() => {
    loadRemovals();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setActiveDropdown(null);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const loadRemovals = () => {
    if (user.role === "Admin" || user.role === "superadmin") {
      dispatch(fetchRemovals());
    } else {
      dispatch(fetchDealerRemovals(user.dealerId));
    }
  };

  const transformedRemovals = useMemo(() => {
    if (!removals || !Array.isArray(removals)) return [];
    
    return removals.map(client => ({
      id: client.id,
      sno: client.sno || '',
      clientName: client.client || '',
      regNum: client.regnum || '',
      dlNum: client.DLnum || '',
      state: client.state || '',
      primaryMobile: client.clientphone1 || '',
      email: client.email || '',
      address: client.clientAddress || '',
      prohibstart: client.prohibstart || '',
      prohibend: client.prohibend || '',
      cbid: client.cbid,
    }));
  }, [removals]);

  const filteredRemovals = useMemo(() => {
    if (!searchTerm) return transformedRemovals;
    
    const searchLower = searchTerm.toLowerCase();
    return transformedRemovals.filter(removal =>
      removal.sno?.toLowerCase().includes(searchLower) ||
      removal.clientName?.toLowerCase().includes(searchLower) ||
      removal.regNum?.toLowerCase().includes(searchLower) ||
      removal.dlNum?.toLowerCase().includes(searchLower) ||
      removal.state?.toLowerCase().includes(searchLower) ||
      removal.primaryMobile?.toLowerCase().includes(searchLower) ||
      removal.email?.toLowerCase().includes(searchLower)
    );
  }, [transformedRemovals, searchTerm]);

  const totalPages = Math.ceil(filteredRemovals.length / rowsPerPage);
  useEffect(() => {
    setCurrentPage(page => Math.min(page, Math.max(1, totalPages)));
  }, [totalPages]);
  const indexOfFirst = (currentPage - 1) * rowsPerPage;
  const currentItems = filteredRemovals.slice(indexOfFirst, indexOfFirst + rowsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
    }
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);
      
      // Adjust if we're at the beginning
      if (currentPage - halfVisible < 1) {
        endPage = Math.min(totalPages, endPage + (halfVisible - currentPage + 1));
      }
      
      // Adjust if we're at the end
      if (currentPage + halfVisible > totalPages) {
        startPage = Math.max(1, startPage - (currentPage + halfVisible - totalPages));
      }
      
      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      
      // Add last page and ellipsis if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const handleRestoreClient = async (item) => {
    const result = await Swal.fire({
      title: `Restore ${item.regNum}?`,
      text: "This vehicle will be moved back to active clients.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Restore',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    try {
      await dispatch(restoreClient(item.id)).unwrap();
      await Swal.fire({
        title: 'Restored!',
        text: 'Vehicle successfully restored to clients',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (err) {
      Swal.fire({
        title: 'Error',
        text: err || 'Failed to restore client',
        icon: 'error'
      });
    }
  };

  const handleUninstallationForm = async (item) => {
    setSelectedClient(item);
    setShowPDFModal(true);
    await dispatch(getUninstallationForm(item.id));
  };

  const handleViewLogs = async (item, type) => {
    setSelectedClient(item);
    setLogType(type);
    setShowLogsModal(true);
    setActiveDropdown(null);
    
    if (type === 'csv') {
      await dispatch(getCSVLogs({ cbid: item.cbid, clientName: item.clientName }));
    } else {
      await dispatch(getTextLogs({ cbid: item.cbid, clientName: item.clientName }));
    }
  };

  const closeLogsModal = () => {
    setShowLogsModal(false);
    setSelectedClient(null);
    setLogType(null);
    dispatch(clearLogs());
  };

  const closePDFModal = () => {
    setShowPDFModal(false);
    setSelectedClient(null);
    dispatch(clearPDF());
  };

  const downloadLogs = () => {
    const content = logType === 'csv' ? csvLogs?.data : textLogs?.data;
    if (!content) return;
    
    const blob = new Blob([content], { 
      type: logType === 'csv' ? 'text/csv' : 'text/plain' 
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedClient?.clientName}_${logType}_log.${logType === 'csv' ? 'csv' : 'txt'}`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleDropdown = (e, id) => {
    e.stopPropagation();
    setActiveDropdown(activeDropdown === id ? null : id);
  };
const renderRemovalsSkeleton = () => {
  const skeletonRows = Array.from({ length: 8 });

  return (
    <div className="removals-container">
      <div className="removals-card">

        {/* HEADER */}
        <div className="removals-header">
          <div>
            <h1>Removals</h1>
            <p>Manage removed vehicles and restoration</p>
          </div>

          <div className="device-skeleton removals-skeleton-count" />
        </div>

        {/* FILTERS */}
        <div className="removals-filters">
          <div className="device-skeleton removals-skeleton-search" />

          <div className="device-skeleton removals-skeleton-show" />
        </div>

        {/* TABLE */}
        <div className="table-wrapper">
          <table className="removals-table">

            <thead>
              <tr>
                <th>SNO</th>
                <th>Client Name</th>
                <th>Reg Number</th>
                <th>DL Number</th>
                <th>State</th>
                <th>Phone</th>
                <th>Email</th>
                 <th>install Date</th>
                <th>Removal Date</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {skeletonRows.map((_, index) => (
                <tr key={index}>

                  <td>
                    <div className="device-skeleton removals-skeleton-sno" />
                  </td>

                  <td>
                    <div className="device-skeleton removals-skeleton-name" />
                  </td>

                  <td>
                    <div className="device-skeleton removals-skeleton-reg" />
                  </td>

                  <td>
                    <div className="device-skeleton removals-skeleton-dl" />
                  </td>

                  <td>
                    <div className="device-skeleton removals-skeleton-state" />
                  </td>

                  <td>
                    <div className="device-skeleton removals-skeleton-phone" />
                  </td>

                  <td>
                    <div className="device-skeleton removals-skeleton-email" />
                  </td>

                  <td>
                    <div className="device-skeleton removals-skeleton-date" />
                  </td>

                  <td>
                    <div className="removals-skeleton-actions">
                      <div className="device-skeleton removals-skeleton-icon" />
                      <div className="device-skeleton removals-skeleton-icon" />
                    </div>
                  </td>

                </tr>
              ))}
            </tbody>

          </table>
        </div>

      </div>
    </div>
  );
};
  if (loading) {
  return renderRemovalsSkeleton();
}

  // if (loading) {
  //   return (
  //     <div className="removals-container">
  //       <div className="loading-state">Loading removals...</div>
  //     </div>
  //   );
  // }

  if (error) {
    return (
      <div className="removals-container">
        <div className="error-state">
          <p>Error: {error}</p>
          <button onClick={loadRemovals}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="removals-container">
      <div className="removals-card">
        {/* Header */}
        <div className="removals-header">
          <div>
            <h1>Removals</h1>
            <p>Manage removed vehicles and restoration</p>
          </div>
          <div className="total-count">
            Total: {filteredRemovals.length}
          </div>
        </div>

        {/* Filters */}
        <div className="removals-filters">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search ..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>×</button>
            )}
          </div>
          <div className="per-page">
            <span>Show</span>
            <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}>
              {[10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
            <span>entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="removals-table">
            <thead>
              <tr>
                <th>SNO</th>
                <th>Client Name</th>
                <th>Reg Number</th>
                <th>DL Number</th>
                <th>State</th>
                <th>Phone</th>
                <th>Email</th>
                <th>install Date</th>
                <th>Removal Date</th>
                <th style={{ textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">No removal records found</td>
                </tr>
              ) : (
                currentItems.map((removal) => (
                  <tr key={removal.id}>
                    <td className="sno">{removal.sno || '-'}</td>
                    <td className="client-name">{removal.clientName || '-'}</td>
                    <td>{removal.regNum || '-'}</td>
                    <td>{removal.dlNum || '-'}</td>
                    <td className="state">
                      <span className="state-badge">{removal.state || '-'}</span>
                    </td>
                    <td>{removal.primaryMobile || '-'}</td>
                    <td className="email">{removal.email || '-'}</td>
                    <td className="email">{removal.prohibstart || '-'}</td>
                    <td>{removal.prohibend?.split(' ')[0] || '-'}</td>
                    <td className="actions">
                      <button 
                        className="action-btn form-btn"
                        onClick={() => handleUninstallationForm(removal)}
                        title="Uninstallation Form"
                      >
                        📄
                      </button>
                      
                      {/* <div className="dropdown">
                        <button 
                          className="action-btn logs-btn"
                          onClick={(e) => toggleDropdown(e, removal.id)}
                          title="View Logs"
                        >
                          📋
                        </button>
                        {activeDropdown === removal.id && (
                          <div className="dropdown-menu">
                            <button onClick={() => handleViewLogs(removal, 'csv')}>CSV Format</button>
                            <button onClick={() => handleViewLogs(removal, 'text')}>TEXT Document</button>
                          </div>
                        )}
                      </div> */}
                      
                      <button 
                        className="action-btn restore-btn"
                        onClick={() => handleRestoreClient(removal)}
                        title="Restore Vehicle"
                      >
                        ↩️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filteredRemovals.length > 0 && (
          <div className="pagination">
            <div className="pagination-info">
              Showing {indexOfFirst + 1} to {Math.min(indexOfFirst + rowsPerPage, filteredRemovals.length)} of {filteredRemovals.length}
            </div>
            <div className="pagination-controls">
              <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>«</button>
              <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
              {getPageNumbers().map((p, i) => (
                p === '...' 
                  ? <span key={`ellipsis-${i}`} className="dots">…</span>
                  : <button key={`page-${p}`} className={currentPage === p ? 'active' : ''} onClick={() => handlePageChange(p)}>{p}</button>
              ))}
              <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
              <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages}>»</button>
            </div>
            <div className="goto">
              <span>Go to</span>
              <form onSubmit={handleGoToPage}>
                <input type="number" min="1" max={totalPages} value={goToPage} onChange={e => setGoToPage(e.target.value)} />
                <button type="submit">Go</button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Logs Modal */}
      {showLogsModal && (
        <div className="modal-overlay" onClick={closeLogsModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{logType?.toUpperCase()} Logs - {selectedClient?.clientName}</h3>
              <button className="modal-close" onClick={closeLogsModal}>×</button>
            </div>
            <div className="modal-body">
              {logsLoading ? (
                <div className="loading-state">Loading logs...</div>
              ) : (
                <pre>{logType === 'csv' ? csvLogs?.data : textLogs?.data}</pre>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeLogsModal}>Close</button>
              <button className="btn-primary" onClick={downloadLogs}>Download</button>
            </div>
          </div>
        </div>
      )}

      {/* PDF Modal */}
      {showPDFModal && (
        <div className="modal-overlay" onClick={closePDFModal}>
          <div className="modal pdf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Uninstallation Form - {selectedClient?.clientName}</h3>
              <button className="modal-close" onClick={closePDFModal}>×</button>
            </div>
            <div className="modal-body">
              {pdfLoading ? (
                <div className="loading-state">Loading PDF...</div>
              ) : pdfUrl ? (
                <iframe src={pdfUrl} title="PDF Viewer" />
              ) : (
                <div className="error-state">Failed to load PDF</div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={closePDFModal}>Close</button>
              {pdfUrl && (
                <a href={pdfUrl} download={`${selectedClient?.clientName}_removal_form.pdf`} className="btn-primary">
                  Download PDF
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Removals;
